/**
 * cloudMigration — the Migrating tab's 2.0 -> 4.0 migrations, shared by the tab and by turning on
 * "Upgraded to 5.0" (Settings > Architecture), which runs them all and then restarts the local
 * server. See docs/CLOUD_SWITCH_MIGRATION.md.
 *
 * Pure builders + one loader + one runner. Each migration is its own short API call (no Lambda
 * fans out), so a long run can't hit the API Gateway timeout; the page shows each step as it goes.
 *
 * Every migration is safe to run twice: the 4.0 comparison below leaves out what is already
 * migrated, and the backend skips (groups, users, reason codes) or updates (alert settings) a row
 * that is already live, so two runs at once can't duplicate either.
 */

import { getAccessSettings } from '../../../../../libs.jsx';
import { apiMqtt } from '../../../../../services/api.js';
import {
  fetchDDBUsers, fetchDDBGroups, fetchDDBAFR, fetchDDBHP,
  fetchDDBAlertSettings, fetchDDBReasonCodes,
  fetchMyUsers, fetchMyGroups, fetchMyAFR, fetchMyHP,
  fetchMyReasonCodes, fetchMyAlertSettings,
  fetchEventTypes, fetchDeviceTypes,
  migrateUsers, migrateGroups, migrateAFRDevices,
  migrateHPAlerts, migrateReasonCodes,
  migrateAlertSettings, migrateCustomSettings,
} from '../api/campusProfileApi.js';

// ── Loading + comparison ─────────────────────────────────────────────────────

/** Every read the comparison needs: 2.0 (DynamoDB), 4.0 (SQL) and the two lookup tables. */
export async function loadMigrationSources(campusId, campusGuid) {
  const r = await Promise.all([
    fetchDDBUsers(campusGuid, campusId),
    fetchDDBGroups(campusGuid, campusId),
    fetchDDBAFR(campusGuid, campusId),
    fetchDDBHP(campusGuid, campusId),
    fetchDDBAlertSettings(campusGuid, campusId),
    fetchDDBReasonCodes(campusGuid, campusId),
    fetchMyUsers(campusId),
    fetchMyGroups(campusId),
    fetchMyAFR(campusId),
    fetchMyHP(campusId),
    fetchMyReasonCodes(campusId),
    fetchMyAlertSettings(campusId),
    fetchEventTypes(campusId),
    fetchDeviceTypes(campusId),
  ]);
  const [ddbUsers, ddbGroups, ddbAFR, ddbHP, ddbAlerts, ddbReasonCodes,
    myUsers, myGroups, myAFR, myHP, myReasonCodes, myAlerts, eventTypes, deviceTypes] = r.map((x) => x || []);
  return {
    ddbUsers, ddbGroups, ddbAFR, ddbHP, ddbAlerts, ddbReasonCodes,
    myUsers, myGroups, myAFR, myHP, myReasonCodes, myAlerts, eventTypes, deviceTypes,
  };
}

/** Users available to migrate = 2.0 users not in 4.0 (by cognito_id/UserId) */
export function availableUsers(ddbUsers, myUsers) {
  const existing = new Set(myUsers.map((u) => u.cognito_id));
  return ddbUsers.filter((u) => u.UserId && !existing.has(u.UserId));
}

/** Groups available to migrate = 2.0 groups not in 4.0 (by group_guid/GroupId) */
export function availableGroups(ddbGroups, myGroups) {
  const existing = new Set(myGroups.map((g) => g.group_guid));
  return ddbGroups.filter((g) => g.GroupId && !existing.has(g.GroupId));
}

/** AFR device types available to migrate -> [dataSet, the 4.0 ids already set] */
export function availableAFR(ddbAFR, myAFR, deviceTypes) {
  const myIds = myAFR.map((a) => a.device_type_id);
  const dataSet = [];
  const afrDevices = ddbAFR.length > 0 && ddbAFR[0]?.AFRDevices ? ddbAFR[0].AFRDevices : [];
  for (const devName of afrDevices) {
    for (const dt of deviceTypes) {
      if (devName.toLowerCase() === dt.device_type_name?.toLowerCase() && !myIds.includes(dt.inovonics_type_id)) {
        dataSet.push({ device_type_id: dt.inovonics_type_id, device_type_name: dt.device_type_name });
        break;
      }
    }
  }
  return [dataSet, myIds];
}

/** High priority alert types available to migrate -> [dataSet, the 4.0 ids already set] */
export function availableHP(ddbHP, myHP, eventTypes) {
  const myIds = myHP.map((h) => h.event_type_id);
  const dataSet = [];
  const hpTypes = ddbHP.length > 0 && ddbHP[0]?.HighPriorityAlertTypes ? ddbHP[0].HighPriorityAlertTypes : [];
  for (const hpName of hpTypes) {
    for (const et of eventTypes) {
      const match =
        (hpName.toLowerCase() === 'alert' && et.event_type_name?.toLowerCase() === 'emergency') ||
        hpName.toLowerCase() === et.event_type_name?.toLowerCase();
      if (match && !myIds.includes(et.event_type_id)) {
        dataSet.push({ event_type_id: et.event_type_id, event_type_name: et.event_type_name });
        break;
      }
    }
  }
  return [dataSet, myIds];
}

/** Reason codes available to migrate -> [dataSet, the 4.0 ids already present] */
export function availableReasons(ddbReasonCodes, myReasonCodes) {
  const myIds = myReasonCodes.map((r) => r.reason_code_id);
  const dataSet = [];
  for (const rc of ddbReasonCodes) {
    const rcId = parseInt(rc.ReasonCodeId, 10);
    if (!myIds.includes(rcId)) {
      dataSet.push({
        reason_code_description: rc.Description,
        reason_code_id: rcId,
        pal_reason_code_id: parseInt(rc.GlobalReasonCode, 10),
      });
    }
  }
  return [dataSet, myIds];
}

/** Alert settings available to migrate (2.0 has settings AND 4.0 does NOT) */
export function alertSettingsAvailable(ddbAlerts, myAlerts) {
  return ddbAlerts.length > 0 && (!myAlerts || myAlerts.length === 0);
}

// ── Request builders ─────────────────────────────────────────────────────────

/* afrSave / prioritySave write to the REQUEST's CampusId (not the row's), as group_add sends its
   server re-sync there. The page's session campus is the signed-in user's own, so these get the
   profile's campus, as V1 Campus.jsx sent it. */
export const profileSession = (session, campusId) => ({ ...session, campusId });

/** user-manage "migrate" rows for these 2.0 users; groups map by guid onto the 4.0 groups. */
export function buildUsersPayload(users, myGroups, { campusId, campusGuid, clientId }) {
  return users.map((user) => {
    let role;
    switch (user.UserRole) {
      case 'admin': role = 5; break;
      case 'corporate': role = 4; break;
      case 'corporate-admin': case 'palcare': role = 3; break;
      case 'palcare-admin': role = 1; break;
      default: role = 6;
    }
    const groupIds = [];
    for (const g of myGroups) {
      if (user.GroupIds && user.GroupIds.includes(g.group_guid) && !groupIds.includes(g.group_id)) {
        groupIds.push(g.group_id);
      }
    }
    return {
      Action: 'migrate',
      DisplayName: user.DisplayName,
      UserEmail: user.UserEmail || user.Email || '',
      UserRole: role,
      UserId: user.UserId,
      Migrated: true,
      CampusGuid: campusGuid,
      PortalAccess: getAccessSettings(role),
      GroupIds: groupIds,
      Username: user.UserName,
      CampusId: campusId,
      ClientId: clientId,
    };
  });
}

export function buildGroupsPayload(groups, campusId) {
  return groups.map((group) => ({
    campus_id: campusId,
    group_name: group.GroupName,
    capcode: group.CapCode,
    group_guid: group.GroupId,
  }));
}

/** afrSave replaces the campus's whole AFR set, so it is the new ids plus the ones already set. */
export function buildAfrPayloads(newIds, originIds, campusId) {
  return {
    payload: [{ campus_id: campusId, afr_entities: [...newIds, ...originIds].map((id) => `${id}-1`) }],
    oldPayload: [{ campusId, afr_entities: originIds }],
  };
}

/** prioritySave replaces the campus's whole set too. */
export function buildHpPayloads(newIds, originIds, campusId) {
  return {
    payload: [{ campus_id: campusId, priority_entities: [...newIds, ...originIds] }],
    oldPayload: [{ campusId, priority_entities: originIds }],
  };
}

export function buildReasonsPayload(reasons, campusId) {
  return reasons.map((r) => ({
    reason_code_description: r.reason_code_description,
    pal_reason_code_id: r.pal_reason_code_id,
    reason_code_id: r.reason_code_id,
    campus_id: campusId,
  }));
}

/** 2.0 CampusConfig -> core_campus_alert_settings (minutes -> seconds) + the two custom flags. */
export function buildAlertPayloads(a, campusId, userId) {
  return {
    alertPayload: [{
      campus_id: campusId,
      accept_required: a.AcceptButtonEnabled ? 1 : 0,
      finish_required: a.FinishButtonEnabled ? 1 : 0,
      reason_required: a.ReasonCodeEnabled ? 1 : 0,
      release_timeout: parseInt(a.ReleaseTimeInterval || 60, 10) * 60,
      finish_timeout: parseInt(a.FinishTimeInterval || 60, 10) * 60,
      clear_timeout: parseInt(a.ClearTimeInterval || 59, 10) * 60,
    }],
    customPayload: [{
      campus_id: campusId,
      is_name_editable: 0,
      is_group_editable: 0,
      modified_by: userId,
    }],
  };
}

/** The alert-settings migration is two writes; both must succeed. */
export async function migrateAlertAndCustomSettings(ddbAlert, campusId, session) {
  const { alertPayload, customPayload } = buildAlertPayloads(ddbAlert, campusId, session.userId);
  const [alertResult, customResult] = await Promise.all([
    migrateAlertSettings(alertPayload, session),
    migrateCustomSettings(customPayload, session),
  ]);
  if (alertResult?.[0] && customResult?.[0]) return [true];
  return [false, (!alertResult?.[0] ? alertResult?.[1] : customResult?.[1]) || ''];
}

// ── Turning on "Upgraded to 5.0" ─────────────────────────────────────────────

/** In run order. `save` is the is_cloud save itself; `sync` is the sync-local server restart. */
export const CLOUD_SWITCH_STEPS = [
  { key: 'save', label: 'Save "Upgraded to 5.0"' },
  { key: 'groups', label: 'Migrate groups' },
  { key: 'users', label: 'Migrate users' },
  { key: 'reasons', label: 'Migrate reason codes' },
  { key: 'afr', label: 'Migrate AFR device types' },
  { key: 'hp', label: 'Migrate high priority alert types' },
  { key: 'alerts', label: 'Migrate alert settings' },
  { key: 'sync', label: 'Restart the local server (Sync Local)' },
];

const MIGRATION_KEYS = ['groups', 'users', 'reasons', 'afr', 'hp', 'alerts'];

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const failText = (result, fallback) => (result?.[1] ? `${fallback}: ${result[1]}` : fallback);

/**
 * Run `keys` (a subset of CLOUD_SWITCH_STEPS, kept in its order), reporting each step through
 * onStep(key, { status: 'running' | 'done' | 'skipped' | 'failed', detail }).
 *
 * Failure policy:
 *   - save fails: stop. Nothing is migrated and the server is not restarted.
 *   - a migration fails: carry on with the rest (they don't depend on each other, except that
 *     users link to whichever groups exist by then).
 *   - sync runs after the migrations even if one failed when `forceSync` (the first run: is_cloud
 *     is saved, and the server only switches over when it restarts). On a retry it runs only if
 *     a retried step migrated something, or if the sync itself is being retried.
 *
 * @returns {Promise<{ failed: string[] }>} the keys that failed
 */
export async function runCloudSwitch({ keys, campusId, campusGuid, session, saveCloud, forceSync, onStep }) {
  const failed = [];
  const run = CLOUD_SWITCH_STEPS.map((s) => s.key).filter((k) => keys.includes(k));
  let migratedSomething = false;
  let src = null;

  const report = (key, status, detail = '') => {
    if (status === 'failed') failed.push(key);
    if (status === 'done' && MIGRATION_KEYS.includes(key)) migratedSomething = true;
    onStep(key, { status, detail });
  };

  for (const key of run) {
    onStep(key, { status: 'running', detail: '' });
    try {
      if (key === 'save') {
        const result = await saveCloud();
        if (!result?.[0]) {
          report('save', 'failed', failText(result, 'The setting was not saved'));
          for (const rest of run.slice(1)) onStep(rest, { status: 'skipped', detail: 'Not run: the setting was not saved.' });
          return { failed };
        }
        report('save', 'done');
        continue;
      }

      if (key === 'sync') {
        if (!forceSync && !migratedSomething) {
          report('sync', 'skipped', 'Nothing new was migrated, so the server was not restarted again.');
          continue;
        }
        const [ok, msg] = await apiMqtt({ operation: 'reboot-server', campusId, clientId: session.clientId });
        if (ok) report('sync', 'done', 'Allow up to 3 minutes for the server to restart and sync.');
        else report('sync', 'failed', msg ? `The restart was not sent: ${msg}` : 'The restart was not sent.');
        continue;
      }

      if (!campusGuid) {
        report(key, 'skipped', 'No 2.0 campus GUID, so there is nothing to migrate.');
        continue;
      }
      // Read once, then fresh 4.0 groups after the groups step so users link to them.
      if (!src) src = await loadMigrationSources(campusId, campusGuid);

      if (key === 'groups') {
        const groups = availableGroups(src.ddbGroups, src.myGroups);
        if (!groups.length) { report(key, 'skipped', 'Nothing to migrate.'); continue; }
        const result = await migrateGroups(buildGroupsPayload(groups, campusId), profileSession(session, campusId));
        src.myGroups = (await fetchMyGroups(campusId)) || src.myGroups;
        report(key, result?.[0] ? 'done' : 'failed',
          result?.[0] ? `${plural(groups.length, 'group', 'groups')} migrated.` : failText(result, 'Group migration failed'));
      } else if (key === 'users') {
        const users = availableUsers(src.ddbUsers, src.myUsers);
        if (!users.length) { report(key, 'skipped', 'Nothing to migrate.'); continue; }
        const payload = buildUsersPayload(users, src.myGroups, { campusId, campusGuid, clientId: session.clientId });
        const result = await migrateUsers(payload, session.userId);
        report(key, result?.[0] ? 'done' : 'failed',
          result?.[0] ? `${plural(users.length, 'user', 'users')} migrated.` : failText(result, 'User migration failed'));
      } else if (key === 'reasons') {
        const [reasons] = availableReasons(src.ddbReasonCodes, src.myReasonCodes);
        if (!reasons.length) { report(key, 'skipped', 'Nothing to migrate.'); continue; }
        const result = await migrateReasonCodes(buildReasonsPayload(reasons, campusId), session);
        report(key, result?.[0] ? 'done' : 'failed',
          result?.[0] ? `${plural(reasons.length, 'reason code', 'reason codes')} migrated.` : failText(result, 'Reason code migration failed'));
      } else if (key === 'afr') {
        const [afr, originIds] = availableAFR(src.ddbAFR, src.myAFR, src.deviceTypes);
        if (!afr.length) { report(key, 'skipped', 'Nothing to migrate.'); continue; }
        const { payload, oldPayload } = buildAfrPayloads(afr.map((a) => a.device_type_id), originIds, campusId);
        const result = await migrateAFRDevices(payload, oldPayload, profileSession(session, campusId));
        report(key, result?.[0] ? 'done' : 'failed',
          result?.[0] ? `${plural(afr.length, 'device type', 'device types')} migrated.` : failText(result, 'AFR migration failed'));
      } else if (key === 'hp') {
        const [hp, originIds] = availableHP(src.ddbHP, src.myHP, src.eventTypes);
        if (!hp.length) { report(key, 'skipped', 'Nothing to migrate.'); continue; }
        const { payload, oldPayload } = buildHpPayloads(hp.map((h) => h.event_type_id), originIds, campusId);
        const result = await migrateHPAlerts(payload, oldPayload, profileSession(session, campusId));
        report(key, result?.[0] ? 'done' : 'failed',
          result?.[0] ? `${plural(hp.length, 'alert type', 'alert types')} migrated.` : failText(result, 'High priority migration failed'));
      } else if (key === 'alerts') {
        if (!alertSettingsAvailable(src.ddbAlerts, src.myAlerts)) {
          report(key, 'skipped', src.ddbAlerts.length ? 'Already set in 4.0.' : 'Nothing to migrate.');
          continue;
        }
        const result = await migrateAlertAndCustomSettings(src.ddbAlerts[0], campusId, session);
        report(key, result[0] ? 'done' : 'failed',
          result[0] ? 'Alert and customization settings migrated.' : failText(result, 'Alert settings migration failed'));
      }
    } catch (err) {
      console.error(`[runCloudSwitch] ${key} failed:`, err);
      report(key, 'failed', 'Request failed. Check your connection and retry.');
    }
  }
  return { failed };
}
